import { createFileRoute } from '@tanstack/react-router';
import { Inbox, MessageSquarePlus } from 'lucide-react';
import { LayoutGroup, motion } from 'motion/react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

import { CaregiverDetailModal } from '@apps/web/components/caregivers/caregiver_detail_modal';
import { ShiftPageDetailsDialog } from '@apps/web/components/dashboard/shift_list_pane';
import {
    ConversationInboxSelectSplit,
    idsForInboxSelectScope,
    nextMasterSelection,
} from '@apps/web/components/inbox/inbox_select_split';
import { PhoneAppBarButton } from '@apps/web/components/layout/phone_app_bar_slots';
import { ContactActivityPanel } from '@apps/web/components/shared/messages/contact_activity_panel';
import { ConversationAgentTracePanel } from '@apps/web/components/shared/messages/conversation_agent_trace_panel';
import { ConversationAssigneePicker } from '@apps/web/components/shared/messages/conversation_assignment';
import { ConversationDraftPane } from '@apps/web/components/shared/messages/conversation_draft_pane';
import { ConversationListItemContent } from '@apps/web/components/shared/messages/conversation_list_item_content';
import { ConversationsPageTabs } from '@apps/web/components/shared/messages/conversations_page_tabs';
import {
    INBOX_PILL_COMPACT_CLASS,
    InboxFilterPill,
} from '@apps/web/components/shared/messages/inbox_filter_pills';
import { ShiftFootnoteOpenProvider } from '@apps/web/components/shared/messages/message_shift_footnotes';
import { OrganizationConversationDetailPane } from '@apps/web/components/shared/messages/organization_conversation_panels';
import { Button } from '@apps/web/components/ui/button';
import { EmptyState } from '@apps/web/components/ui/empty_state';
import { FilterBar, type FilterChip } from '@apps/web/components/ui/filter_bar';
import { InboxListRow } from '@apps/web/components/ui/inbox_list_row';
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from '@apps/web/components/ui/resizable';
import { SearchInput } from '@apps/web/components/ui/search_input';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import {
    conversationInboxActiveFilterTypes,
    describeConversationInboxChipChanges,
    useConversationsInboxAnalytics,
} from '@apps/web/hooks/use_conversations_inbox_analytics';
import { useDeepLinkOrganization } from '@apps/web/hooks/use_deep_link_organization';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import {
    prefetchResourceContext,
    useResourceOrganizationLookup,
} from '@apps/web/hooks/use_resource_organization_lookup';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { cn } from '@apps/web/lib/utils';
import { useUser } from '@apps/web/swr_data_fetching/current_user_queries';
import {
    useAgentRunForConversation,
    useOrganizationVoiceCall,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { useOrganization } from '@apps/web/swr_data_fetching/organization_queries';

import { ConversationInboxBulkBar } from '../conversations-inbox-bulk-bar';
import {
    type ConversationsInboxSortKey,
    useConversationsInboxController,
} from '../conversations-inbox-controller';
import {
    INBOX_TOOLBAR_FIT_STEPS,
    useFitToolbar,
} from '../conversations-inbox-fit';
import { useInboxArrowNavigation } from '../conversations-inbox-keyboard';
import { ConversationInboxMenus } from '../conversations-inbox-menus';
import {
    ConversationInboxRowAvatar,
    ConversationInboxRowContextMenu,
} from '../conversations-inbox-row';

export const conversationsSearchSchema = z.object({
    conversationId: z.string().optional(),
    messageId: z.string().optional(),
    // Deep-link entry (e.g. activity board): resolve caregiver's newest thread.
    caregiverId: z.string().optional(),
    clientId: z.string().optional(),
    contactPhone: z.string().optional(),
    // From the Calls tab: open this call's dialog on top of the thread.
    voiceCallId: z.string().optional(),
    organizationId: z.string().optional(),
    mode: z.enum(['live', 'sandbox']).optional(),
});

export const Route = createFileRoute('/_app/(main)/conversations/')({
    beforeLoad: ({ cause, search }) => {
        // A link without an org must first learn which org owns the
        // conversation. Start that lookup now instead of after the
        // organization and layout queries that gate the inbox.
        if (
            cause === 'enter' &&
            search.conversationId &&
            !search.organizationId
        ) {
            prefetchResourceContext('conversation', search.conversationId);
        }
    },
    component: ConversationsInboxRouteEntry,
    validateSearch: conversationsSearchSchema,
    head: () => ({
        meta: [{ title: 'Conversations | Phoebe' }],
    }),
});

function ConversationsInboxRouteEntry() {
    const {
        conversationId,
        messageId,
        caregiverId,
        clientId,
        contactPhone,
        voiceCallId,
        organizationId: deepLinkOrganizationId,
        mode: deepLinkMode,
    } = Route.useSearch();
    const { isWaitingForDeepLinkOrganization } = useDeepLinkOrganization(
        deepLinkOrganizationId,
        deepLinkMode,
    );
    // Only the conversation the route was entered with can belong to another
    // org; ids the mounted inbox writes on row selection are already local.
    const [enteredConversationId] = useState(conversationId);
    const { isResolvingAcrossOrganizations } = useResourceOrganizationLookup({
        enabled:
            !!enteredConversationId &&
            !deepLinkOrganizationId &&
            !isWaitingForDeepLinkOrganization,
        resourceId: isWaitingForDeepLinkOrganization
            ? undefined
            : enteredConversationId,
        resourceType: 'conversation',
    });
    if (isWaitingForDeepLinkOrganization || isResolvingAcrossOrganizations) {
        return null;
    }
    return (
        <ConversationsInboxRoute
            caregiverId={caregiverId}
            clientId={clientId}
            contactPhone={contactPhone}
            conversationId={conversationId}
            messageId={messageId}
            voiceCallId={voiceCallId}
        />
    );
}

// Conversations inbox v0. Compact filter pills: Quo-style select, Unread,
// Needs review (pending staged Phoebe reply), then Filter. Staging still
// shows on the row. There is no saved-views row.

const LIVE_SHIFT_ID =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isLiveShiftId(shiftId: string): boolean {
    return LIVE_SHIFT_ID.test(shiftId);
}

function toggleSelectionId(
    current: ReadonlyArray<string>,
    id: string,
): ReadonlyArray<string> {
    return current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id];
}

function ConversationsInboxRoute({
    conversationId: deepLinkConversationId,
    messageId: deepLinkMessageId,
    caregiverId: deepLinkCaregiverId,
    clientId: deepLinkClientId,
    contactPhone: deepLinkContactPhone,
    voiceCallId: deepLinkVoiceCallId,
}: {
    readonly conversationId: string | undefined;
    readonly messageId: string | undefined;
    readonly caregiverId: string | undefined;
    readonly clientId: string | undefined;
    readonly contactPhone: string | undefined;
    readonly voiceCallId: string | undefined;
}) {
    const navigate = Route.useNavigate();
    const { formatName } = useFormatPersonName();
    const {
        activityPanelOpen,
        assign,
        bulkAssignee,
        caregiverEhrLink,
        chips,
        clearSelectedConversation,
        filterDimensions,
        hasSelection,
        highlightMessageId,
        openVoiceCallId,
        initialThreadSearch,
        isBulkReadPending,
        isEmpty,
        isLoadingMore,
        isMobile,
        isComposingNew,
        startNewConversation,
        cancelNewConversation,
        isDraftSelected,
        draftRecipient,
        selectDraft,
        isReachingEnd,
        listLayoutTransition,
        loadMoreConversations,
        mutateConversations,
        needsReviewOnly,
        onBulkAssign,
        onBulkMarkReadState,
        onInboxLinkCapture,
        openConversation,
        openCreatedConversation,
        openFootnoteShiftId,
        organizationId,
        ownershipEnabled,
        markRowReadState,
        persistMarkAsRead,
        persistMarkAsUnread,
        resolvePendingReply,
        profileCaregiverId,
        recentWindowDays,
        recencyFooter,
        rows,
        search,
        searchNotice,
        debouncedSearch,
        selectedConversation,
        selectedConversations,
        selectedId,
        selection,
        setActivityPanelOpen,
        setChips,
        setNeedsReviewOnly,
        setOpenFootnoteShiftId,
        setProfileCaregiverId,
        setSearch,
        setSelection,
        setSort,
        setUnreadOnly,
        sort,
        timezone,
        unreadCount,
        unreadOnly,
    } = useConversationsInboxController({
        conversationId: deepLinkConversationId,
        messageId: deepLinkMessageId,
        caregiverId: deepLinkCaregiverId,
        clientId: deepLinkClientId,
        contactPhone: deepLinkContactPhone,
        voiceCallId: deepLinkVoiceCallId,
    });
    const { data: user } = useUser({ reason: 'conversations-agent-trace' });
    // Admin-only, and only for client-agent conversations: the panel shares
    // the timeline's column, so opening one closes the other.
    const { agentRunId, isClientAgentRun } = useAgentRunForConversation(
        selectedId,
        Boolean(user?.admin),
    );
    const [agentTracePanelOpen, setAgentTracePanelOpen] = useState(false);
    const { data: organization } = useOrganization(organizationId, {
        reason: 'conversations-recipient-type',
    });
    // Once the client agent texts clients, the list mixes clients and
    // caregivers, so each row says which one it is.
    const showRecipientType = useFeatureFlag(
        'client_agent_allowed',
        organization?.features,
    );
    const traceRunId =
        agentTracePanelOpen && isClientAgentRun ? agentRunId : null;
    // Draft is its own selection: show the detail column without a conversation id.
    const showDetailPane = selectedId !== null || isComposingNew;
    // A call deep link whose person has no thread lands in the draft pane,
    // and the call is the draft's only entry.
    const { data: draftCall } = useOrganizationVoiceCall(
        isComposingNew ? organizationId : null,
        openVoiceCallId,
        { reason: 'conversations-draft-call' },
    );
    const analytics = useConversationsInboxAnalytics({ organizationId });
    const filterNavigation = useInboxArrowNavigation<HTMLDivElement>({
        orientation: 'horizontal',
        stopSelector: 'button:not([disabled])',
    });
    useFitToolbar(filterNavigation.containerRef, INBOX_TOOLBAR_FIT_STEPS);
    // Only the row's open button is a stop: stepping through would otherwise
    // land on each row's checkbox on the way past it.
    const listNavigation = useInboxArrowNavigation<HTMLDivElement>({
        orientation: 'vertical',
        stopSelector: '[data-inbox-row-open]',
    });
    // The draft is added at the top; a list scrolled down would otherwise put
    // it out of sight the moment it appears.
    useEffect(() => {
        if (isComposingNew) {
            listNavigation.containerRef.current?.scrollTo({ top: 0 });
        }
    }, [isComposingNew, listNavigation.containerRef]);
    const activeFilterTypes = useMemo(
        () =>
            conversationInboxActiveFilterTypes({
                unreadOnly,
                needsReviewOnly,
                chips,
                sort,
            }),
        [chips, needsReviewOnly, sort, unreadOnly],
    );
    // Similar matches come last in the results; a label keeps them from
    // reading as matches for the words typed.
    const firstSimilarMatchId = rows.find(
        (row) => row.search_match?.kind === 'similar',
    )?.id;
    const inboxViewedRef = useRef(false);
    const skippedInitialSearchRef = useRef(false);
    const openedFromRef = useRef<'list' | 'auto'>('auto');
    const lastOpenedIdRef = useRef<string | null>(null);
    const resultCountRef = useRef(rows.length);
    resultCountRef.current = rows.length;
    const hadActiveFiltersRef = useRef(
        activeFilterTypes.some((type) => type !== 'sort'),
    );
    hadActiveFiltersRef.current = activeFilterTypes.some(
        (type) => type !== 'sort',
    );

    useEffect(() => {
        if (inboxViewedRef.current || organizationId === null) {
            return;
        }
        inboxViewedRef.current = true;
        analytics.captureInboxViewed({
            unreadCount: unreadCount ?? null,
            conversationCount: rows.length,
        });
    }, [analytics, organizationId, rows.length, unreadCount]);

    useEffect(() => {
        if (!skippedInitialSearchRef.current) {
            skippedInitialSearchRef.current = true;
            return;
        }
        analytics.captureSearchUsed({
            queryLength: debouncedSearch.length,
            resultCount: resultCountRef.current,
            hadActiveFilters: hadActiveFiltersRef.current,
        });
    }, [analytics, debouncedSearch]);

    useEffect(() => {
        if (organizationId === null || selectedId === null) {
            return;
        }
        if (lastOpenedIdRef.current === selectedId) {
            return;
        }
        const conversation = rows.find((row) => row.id === selectedId);
        if (conversation === undefined) {
            return;
        }
        lastOpenedIdRef.current = selectedId;
        analytics.captureThreadOpened({
            conversationId: selectedId,
            hadUnread: conversation.unread_count > 0,
            hadPendingReview: conversation.pending_staged_message_count > 0,
            openedFrom: openedFromRef.current,
            activeFilterTypes,
        });
        openedFromRef.current = 'auto';
    }, [activeFilterTypes, analytics, organizationId, rows, selectedId]);

    function nextActiveFilterTypes(
        overrides: Partial<{
            unreadOnly: boolean;
            needsReviewOnly: boolean;
            chips: ReadonlyArray<FilterChip>;
            sort: ConversationsInboxSortKey;
        }>,
    ) {
        return conversationInboxActiveFilterTypes({
            unreadOnly,
            needsReviewOnly,
            chips,
            sort,
            ...overrides,
        });
    }

    function handleChipsChange(next: Array<FilterChip>): void {
        const changes = describeConversationInboxChipChanges(
            chips,
            next,
            filterDimensions,
        );
        setChips(next);
        const nextActive = nextActiveFilterTypes({ chips: next });
        for (const change of changes) {
            analytics.captureFilterChanged({
                ...change,
                activeFilterTypes: nextActive,
                resultCount: rows.length,
            });
        }
    }

    function handleClearChips(): void {
        analytics.captureFiltersCleared({
            clearedFilterCount: new Set(chips.map((chip) => chip.dimension))
                .size,
            hadSearch: search.trim().length > 0,
        });
        setChips([]);
    }

    const newMessageAction = (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <Button
                    aria-label="New message"
                    className="h-8 w-8 shrink-0 shadow-none"
                    onClick={() => startNewConversation()}
                    size="icon"
                    type="button"
                    variant="ghost"
                >
                    <MessageSquarePlus className="h-4 w-4" />
                </Button>
            </TooltipTrigger>
            <TooltipContent>New message</TooltipContent>
        </Tooltip>
    );
    const phoneNewMessageAction = showDetailPane ? undefined : (
        <PhoneAppBarButton
            label="New message"
            onClick={() => startNewConversation()}
        >
            <MessageSquarePlus className="size-5" />
        </PhoneAppBarButton>
    );

    return (
        <div
            className="bg-background relative flex h-full min-h-0 w-full overflow-hidden"
            data-testid="conversations-inbox-workspace"
            onClickCapture={onInboxLinkCapture}
        >
            {/* ---------------- list column ---------------- */}
            <div
                // Stable e2e anchor inherited from the pre-redesign inbox. The
                // maintained Playwright specs locate the list column by this id,
                // so it outlives the layout it was first attached to.
                data-testid="conversation-list-shell"
                className={cn(
                    // The column grows with the window past 24rem so a zoomed-out browser
                    // (whose minimum font size keeps the pill text from shrinking)
                    // gets the room its filter row needs. Capped so a wide monitor
                    // does not turn the list into a second thread pane.
                    '@container/inbox relative h-full min-h-0 w-full shrink-0 flex-col overflow-hidden border-r md:flex md:w-[max(24rem,27vw)] md:max-w-[36rem]',
                    // Mobile: list until a thread or draft is open; then swap.
                    showDetailPane ? 'hidden' : 'flex',
                )}
            >
                <div className="flex-shrink-0 space-y-3 px-3 pt-4 pb-3">
                    <div className="px-1">
                        <ConversationsPageTabs
                            active="messages"
                            actions={newMessageAction}
                            phoneActions={phoneNewMessageAction}
                        />
                    </div>
                    <SearchInput
                        containerClassName="w-full min-w-0"
                        onValueChange={setSearch}
                        placeholder="Search…"
                        value={search}
                    />
                    {/* Left/Right steps along the filters; Tab, Space and
                        Enter keep doing what they do on any button. */}
                    <div
                        aria-label="Conversation filters"
                        // One line, never clipped, scrolled, or wrapped: the
                        // column scales with the window and useFitToolbar
                        // trims the pills when text outgrows it. Order by
                        // takes the trailing edge. The negative margins let
                        // focus rings clear the card padding.
                        className="group/fit -mx-2 -my-1 flex items-center gap-1 px-2 py-1 group-data-[fit-compact]/fit:gap-0.5 @max-[23rem]/inbox:gap-0.5"
                        onKeyDown={filterNavigation.handleKeyDown}
                        ref={filterNavigation.containerRef}
                        role="toolbar"
                    >
                        <ConversationInboxSelectSplit
                            hasSelection={hasSelection}
                            onSelectScope={(scope) => {
                                setSelection(
                                    idsForInboxSelectScope(rows, scope),
                                );
                            }}
                            onToggleMaster={() => {
                                setSelection((current) =>
                                    nextMasterSelection(
                                        current,
                                        rows.map((row) => row.id),
                                    ),
                                );
                            }}
                        />
                        <InboxFilterPill
                            active={unreadOnly}
                            label="Unread"
                            onClick={() => {
                                const next = !unreadOnly;
                                setUnreadOnly(next);
                                analytics.captureFilterChanged({
                                    filterType: 'unread',
                                    action: next ? 'toggle_on' : 'toggle_off',
                                    activeFilterTypes: nextActiveFilterTypes({
                                        unreadOnly: next,
                                    }),
                                });
                            }}
                        />
                        <InboxFilterPill
                            active={needsReviewOnly}
                            label="Needs Review"
                            onClick={() => {
                                const next = !needsReviewOnly;
                                setNeedsReviewOnly(next);
                                analytics.captureFilterChanged({
                                    filterType: 'needs_review',
                                    action: next ? 'toggle_on' : 'toggle_off',
                                    activeFilterTypes: nextActiveFilterTypes({
                                        needsReviewOnly: next,
                                    }),
                                });
                            }}
                        />
                        <ConversationInboxMenus
                            chips={[...chips]}
                            dimensions={filterDimensions}
                            onChipsChange={handleChipsChange}
                            onSortChange={(next) => {
                                setSort(next);
                                analytics.captureFilterChanged({
                                    filterType: 'sort',
                                    action: 'set',
                                    value: next,
                                    activeFilterTypes: nextActiveFilterTypes({
                                        sort: next,
                                    }),
                                });
                            }}
                            pillClassName={INBOX_PILL_COMPACT_CLASS}
                            sort={sort}
                        />
                    </div>
                    <FilterBar
                        chips={[...chips]}
                        dimensions={filterDimensions}
                        layout="active-row"
                        onChange={handleChipsChange}
                        onClear={handleClearChips}
                        showClear={chips.length > 0}
                    />
                </div>
                <div
                    className={cn(
                        'min-h-0 flex-1 overflow-y-auto px-2 pb-2',
                        hasSelection
                            ? 'pb-24'
                            : 'max-md:pb-[calc(0.5rem+var(--phone-launcher-clearance))]',
                    )}
                    onKeyDown={listNavigation.handleKeyDown}
                    ref={listNavigation.containerRef}
                >
                    {isEmpty ? (
                        <Fragment>
                            {/* One phrase for every way the list can come
                                up empty. "No matches" reads as a search
                                result and told a coordinator nothing about
                                which of Unread, Needs review, a filter, or
                                the recency window was hiding their threads.
                                Same copy as the pre-redesign inbox. */}
                            <EmptyState
                                className="mt-12"
                                icon={Inbox}
                                title={
                                    recentWindowDays !== null
                                        ? 'No recent conversations'
                                        : 'No conversations found'
                                }
                            />
                            {recencyFooter}
                        </Fragment>
                    ) : (
                        <section aria-label="Conversations">
                            <LayoutGroup>
                                <ul className="space-y-0.5">
                                    {isComposingNew ? (
                                        <li>
                                            {/* Above everything and ignoring
                                                every filter: a draft is not a
                                                row of data. It stays listed
                                                while other threads are read,
                                                so an unfinished message is
                                                never one click from gone. */}
                                            <button
                                                className={cn(
                                                    'focus-visible:ring-ring/60 flex min-h-[58px] w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left outline-none focus-visible:ring-2',
                                                    isDraftSelected
                                                        ? 'bg-accent'
                                                        : 'hover:bg-muted/40',
                                                )}
                                                data-testid="conversations-inbox-draft-row"
                                                onClick={selectDraft}
                                                type="button"
                                            >
                                                <div className="bg-muted flex size-9 shrink-0 items-center justify-center rounded-full">
                                                    <MessageSquarePlus className="text-muted-foreground size-4" />
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-primary truncate text-sm font-semibold">
                                                        New conversation
                                                    </p>
                                                    <p className="text-muted-foreground truncate text-xs">
                                                        Send a message…
                                                    </p>
                                                </div>
                                            </button>
                                        </li>
                                    ) : null}
                                    {rows.map((conversation) => {
                                        const picked = selection.includes(
                                            conversation.id,
                                        );
                                        return (
                                            <Fragment key={conversation.id}>
                                                {conversation.id ===
                                                firstSimilarMatchId ? (
                                                    <motion.li
                                                        className="text-muted-foreground px-2.5 pt-3 pb-1 text-[10px] font-medium tracking-wide uppercase"
                                                        layout={true}
                                                        transition={
                                                            listLayoutTransition
                                                        }
                                                    >
                                                        Similar messages
                                                    </motion.li>
                                                ) : null}
                                                <motion.li
                                                    layout={true}
                                                    transition={
                                                        listLayoutTransition
                                                    }
                                                >
                                                    <ConversationInboxRowContextMenu
                                                        canAssign={
                                                            ownershipEnabled
                                                        }
                                                        isUnread={
                                                            conversation.unread_count >
                                                            0
                                                        }
                                                        onAssign={(userId) => {
                                                            analytics.captureBulkAction(
                                                                {
                                                                    action: 'assign',
                                                                    selectedCount:
                                                                        picked
                                                                            ? selection.length
                                                                            : 1,
                                                                },
                                                            );
                                                            if (picked) {
                                                                void onBulkAssign(
                                                                    userId,
                                                                );
                                                                return;
                                                            }
                                                            void assign(
                                                                conversation.id,
                                                                userId,
                                                            );
                                                        }}
                                                        onMarkReadState={(
                                                            unreadCount,
                                                        ) => {
                                                            // The bottom bar
                                                            // reports these; the
                                                            // menu has to as
                                                            // well, or the same
                                                            // action counts only
                                                            // when taken one of
                                                            // the two ways.
                                                            analytics.captureBulkAction(
                                                                {
                                                                    action:
                                                                        unreadCount ===
                                                                        0
                                                                            ? 'mark_read'
                                                                            : 'mark_unread',
                                                                    selectedCount:
                                                                        picked
                                                                            ? selection.length
                                                                            : 1,
                                                                },
                                                            );
                                                            if (picked) {
                                                                void onBulkMarkReadState(
                                                                    unreadCount,
                                                                );
                                                                return;
                                                            }
                                                            markRowReadState(
                                                                conversation.id,
                                                                unreadCount,
                                                            );
                                                        }}
                                                        organizationId={
                                                            organizationId
                                                        }
                                                        selectedCount={
                                                            picked
                                                                ? selection.length
                                                                : 0
                                                        }
                                                    >
                                                        <InboxListRow
                                                            active={
                                                                selectedConversation?.id ===
                                                                conversation.id
                                                            }
                                                            picked={picked}
                                                        >
                                                            <ConversationInboxRowAvatar
                                                                conversation={
                                                                    conversation
                                                                }
                                                                onToggle={() =>
                                                                    setSelection(
                                                                        (
                                                                            current,
                                                                        ) =>
                                                                            toggleSelectionId(
                                                                                current,
                                                                                conversation.id,
                                                                            ),
                                                                    )
                                                                }
                                                                picked={picked}
                                                                selecting={
                                                                    hasSelection
                                                                }
                                                            />
                                                            <button
                                                                // Arrow keys land here, so
                                                                // the focus ring follows the
                                                                // row's shape rather than the
                                                                // browser's default box.
                                                                className="focus-visible:ring-ring/60 touch-hit-area min-w-0 flex-1 cursor-pointer rounded-md text-left outline-none focus-visible:ring-2"
                                                                data-inbox-row-open={
                                                                    true
                                                                }
                                                                onClick={() => {
                                                                    openedFromRef.current =
                                                                        'list';
                                                                    openConversation(
                                                                        conversation.id,
                                                                        conversation.search_match ??
                                                                            null,
                                                                    );
                                                                    if (
                                                                        conversation.unread_count >
                                                                        0
                                                                    ) {
                                                                        markRowReadState(
                                                                            conversation.id,
                                                                            0,
                                                                        );
                                                                    }
                                                                }}
                                                                type="button"
                                                            >
                                                                <ConversationListItemContent
                                                                    conversation={
                                                                        conversation
                                                                    }
                                                                    hideAvatar={
                                                                        true
                                                                    }
                                                                    searchQuery={
                                                                        debouncedSearch
                                                                    }
                                                                    showOwnership={
                                                                        ownershipEnabled
                                                                    }
                                                                    showPendingReviewIndicator={
                                                                        true
                                                                    }
                                                                    showRecipientType={
                                                                        showRecipientType
                                                                    }
                                                                    showTrailingUnreadDot={
                                                                        true
                                                                    }
                                                                    timezone={
                                                                        timezone
                                                                    }
                                                                />
                                                            </button>
                                                        </InboxListRow>
                                                    </ConversationInboxRowContextMenu>
                                                </motion.li>
                                            </Fragment>
                                        );
                                    })}
                                </ul>
                                {!isReachingEnd ? (
                                    <div className="px-2 py-3 text-center">
                                        <Button
                                            aria-label={
                                                isLoadingMore
                                                    ? 'Loading…'
                                                    : undefined
                                            }
                                            disabled={isLoadingMore}
                                            onClick={loadMoreConversations}
                                            size="sm"
                                            type="button"
                                            variant="ghost"
                                        >
                                            {isLoadingMore ? (
                                                <Spinner tone="muted" />
                                            ) : (
                                                'Load more'
                                            )}
                                        </Button>
                                    </div>
                                ) : null}
                                {searchNotice}
                                {recencyFooter}
                            </LayoutGroup>
                        </section>
                    )}
                </div>
                {hasSelection && organizationId ? (
                    <ConversationInboxBulkBar
                        assignControl={
                            ownershipEnabled ? (
                                <ConversationAssigneePicker
                                    assignedToUserId={bulkAssignee.userId}
                                    assignedToUserName={bulkAssignee.userName}
                                    conversationId={
                                        selectedConversations[0]?.id ?? ''
                                    }
                                    onAssign={(userId) => {
                                        analytics.captureBulkAction({
                                            action: 'assign',
                                            selectedCount: selection.length,
                                        });
                                        void onBulkAssign(userId);
                                    }}
                                    organizationId={organizationId}
                                    triggerAriaLabel="Assign to"
                                    triggerClassName="size-7 rounded-md md:size-7 text-zinc-100 hover:bg-white/10 hover:text-zinc-100"
                                />
                            ) : null
                        }
                        isPending={isBulkReadPending}
                        onClearSelection={() => setSelection([])}
                        onMarkAsRead={() => {
                            analytics.captureBulkAction({
                                action: 'mark_read',
                                selectedCount: selection.length,
                            });
                            void onBulkMarkReadState(0);
                        }}
                        onMarkAsUnread={() => {
                            analytics.captureBulkAction({
                                action: 'mark_unread',
                                selectedCount: selection.length,
                            });
                            void onBulkMarkReadState(1);
                        }}
                        selectedCount={selection.length}
                    />
                ) : null}
            </div>
            {/* ---------------- detail pane ---------------- */}
            <div
                // Stable e2e anchor inherited from the pre-redesign inbox. The
                // maintained Playwright specs locate the thread pane by this id,
                // so it outlives the layout it was first attached to.
                data-testid="conversation-thread-shell"
                className={cn(
                    'min-h-0 min-w-0 flex-1 flex-col md:flex',
                    showDetailPane ? 'flex' : 'hidden',
                )}
            >
                {/* Keep the draft pane mounted for the life of isComposingNew so
                    recipient/composer local state survives opening another
                    thread. Hide it with CSS while a conversation is selected. */}
                {isComposingNew && organizationId ? (
                    <div
                        className={cn(
                            'min-h-0 min-w-0 flex-1 flex-col',
                            isDraftSelected ? 'flex' : 'hidden',
                        )}
                    >
                        <ConversationDraftPane
                            call={
                                draftCall?.kind === 'conversation'
                                    ? draftCall
                                    : null
                            }
                            initialRecipient={draftRecipient}
                            onCancel={cancelNewConversation}
                            onMessageCreated={(conversationId) => {
                                openCreatedConversation(conversationId);
                                void mutateConversations();
                            }}
                            organizationId={organizationId}
                            timezone={timezone}
                        />
                    </div>
                ) : null}
                {selectedId && organizationId ? (
                    <ShiftFootnoteOpenProvider
                        onOpenShift={(shift) => {
                            if (!isLiveShiftId(shift.shift_id)) {
                                return;
                            }
                            analytics.captureShiftOverlayOpened({
                                conversationId: selectedId,
                                shiftId: shift.shift_id,
                            });
                            setOpenFootnoteShiftId(shift.shift_id);
                        }}
                    >
                        {/* The trace splits the thread column, so the
                            thread and the agent's reasoning read side by
                            side. The thread panel stays mounted either way. */}
                        <ResizablePanelGroup
                            className="min-h-0 flex-1"
                            orientation="horizontal"
                        >
                            <ResizablePanel
                                id="conversation-thread"
                                minSize="16rem"
                            >
                                <OrganizationConversationDetailPane
                                    activityPanelOpen={activityPanelOpen}
                                    agentTracePanelOpen={traceRunId !== null}
                                    conversationId={selectedId}
                                    externalLinkHref={
                                        caregiverEhrLink?.href ?? null
                                    }
                                    externalLinkLabel={
                                        caregiverEhrLink?.label ?? null
                                    }
                                    fillThreadWidth={true}
                                    highlightMessageId={
                                        highlightMessageId ?? null
                                    }
                                    openVoiceCallId={openVoiceCallId}
                                    onOpenVoiceCall={(voiceCallId) => {
                                        void navigate({
                                            to: '/conversations',
                                            search: (previous) => ({
                                                ...previous,
                                                voiceCallId,
                                            }),
                                            replace: true,
                                        });
                                    }}
                                    onOpenVoiceCallDismissed={() => {
                                        void navigate({
                                            to: '/conversations',
                                            search: (previous) => ({
                                                ...previous,
                                                voiceCallId: undefined,
                                            }),
                                            replace: true,
                                        });
                                    }}
                                    initialSearch={initialThreadSearch}
                                    markAsRead={persistMarkAsRead}
                                    markAsUnread={persistMarkAsUnread}
                                    onPendingReplyResolved={resolvePendingReply}
                                    onAssignConversation={
                                        ownershipEnabled
                                            ? (conversationId, userId) => {
                                                  analytics.captureAssigned({
                                                      conversationId,
                                                      assignedToUserId: userId,
                                                  });
                                                  void assign(
                                                      conversationId,
                                                      userId,
                                                  );
                                              }
                                            : undefined
                                    }
                                    onBack={
                                        isMobile
                                            ? () => {
                                                  clearSelectedConversation();
                                              }
                                            : undefined
                                    }
                                    onOpenCaregiverProfile={(caregiverId) => {
                                        setProfileCaregiverId(caregiverId);
                                    }}
                                    onToggleActivityPanel={
                                        ownershipEnabled
                                            ? () => {
                                                  setAgentTracePanelOpen(false);
                                                  setActivityPanelOpen(
                                                      (open) => !open,
                                                  );
                                              }
                                            : undefined
                                    }
                                    onToggleAgentTracePanel={() => {
                                        setActivityPanelOpen(false);
                                        setAgentTracePanelOpen(
                                            traceRunId === null,
                                        );
                                    }}
                                    organizationId={organizationId}
                                    ownershipEnabled={ownershipEnabled}
                                    selectedConversation={selectedConversation}
                                />
                            </ResizablePanel>
                            {traceRunId ? (
                                <Fragment>
                                    <ResizableHandle
                                        aria-label="Resize agent trace"
                                        className="hover:bg-primary/50 focus-visible:bg-primary/50 after:w-3"
                                        withHandle={true}
                                    />
                                    <ResizablePanel
                                        defaultSize="45%"
                                        id="conversation-agent-trace"
                                        minSize="14rem"
                                    >
                                        <ConversationAgentTracePanel
                                            onClose={() =>
                                                setAgentTracePanelOpen(false)
                                            }
                                            runId={traceRunId}
                                        />
                                    </ResizablePanel>
                                </Fragment>
                            ) : null}
                        </ResizablePanelGroup>
                    </ShiftFootnoteOpenProvider>
                ) : !(isComposingNew && isDraftSelected) ? (
                    <EmptyState
                        className="m-auto"
                        description="Pick a thread on the left."
                        icon={Inbox}
                        title="No conversation selected"
                    />
                ) : null}
            </div>
            {ownershipEnabled &&
            activityPanelOpen &&
            selectedConversation &&
            organizationId ? (
                // The list is 24rem and the timeline is 24rem, so as a third
                // column the pair eat a 768px window whole and the thread is
                // crushed to a word per line. Below 1280px the timeline
                // overlays the thread instead of competing with it, which
                // keeps the toggle meaningful at every width.
                <div className="bg-background absolute inset-y-0 right-0 left-0 z-30 flex h-full min-h-0 flex-col overflow-hidden border-l shadow-xl md:left-96 xl:static xl:w-96 xl:shrink-0 xl:shadow-none">
                    <ContactActivityPanel
                        contactName={
                            selectedConversation.caregiver_name
                                ? formatName(
                                      selectedConversation.caregiver_name,
                                  )
                                : (selectedConversation.external_contact_name ??
                                  null)
                        }
                        conversationId={selectedConversation.id}
                        onClose={() => setActivityPanelOpen(false)}
                        organizationId={organizationId}
                        timeZone={timezone}
                    />
                </div>
            ) : null}
            {organizationId ? (
                <CaregiverDetailModal
                    caregiverId={profileCaregiverId ?? undefined}
                    isOpen={profileCaregiverId !== null}
                    onClose={() => setProfileCaregiverId(null)}
                    organizationId={organizationId}
                />
            ) : null}
            {organizationId ? (
                <ShiftPageDetailsDialog
                    isOpen={openFootnoteShiftId !== null}
                    onClose={() => setOpenFootnoteShiftId(null)}
                    organizationId={organizationId}
                    shiftId={openFootnoteShiftId}
                />
            ) : null}
        </div>
    );
}
